import { useState, useEffect } from "react";
import { VectorMap } from "react-jvectormap";
import { makeStyles } from "@material-ui/core/styles";
import Icon from "@material-ui/core/Icon";
import { DateRange } from "components/MaterialSymbol/MaterialIcons";
import Paper from "@material-ui/core/Paper";
import MuiTable from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Link, Typography } from "@material-ui/core";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import LegacyTable from "components/Table/Table.js";
import OrderFilterSelector from "components/OrderFilterSelector/OrderFilterSelector";
import MaterialSymbol from "components/MaterialSymbol/MaterialSymbol";

import DashboardService from "../../services/DashboardService";
import OrderService from "../../services/OrderService";

import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { financial } from "helpers";
import useDealer from "hooks/useDealer";
import moment from "moment";
import { isAdminUiSkinEnabled } from "adminUiSkin";

const useStyles = makeStyles(styles);
const useShadcnStyles = makeStyles((theme) => ({
    page: {
        paddingTop: 0,
    },
    header: {
        alignItems: "center",
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 26,
    },
    title: {
        color: theme.palette.text.primary,
        fontSize: 28,
        fontWeight: 700,
        lineHeight: 1.2,
        margin: 0,
    },
    filterCard: {
        borderRadius: 20,
        marginBottom: 30,
        padding: "24px 28px 20px",
    },
    statsGrid: {
        display: "grid",
        gap: 30,
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        marginBottom: 32,
        [theme.breakpoints.down("md")]: {
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        },
        [theme.breakpoints.down("xs")]: {
            gridTemplateColumns: "1fr",
        },
    },
    statCard: {
        alignItems: "center",
        borderRadius: 18,
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        minHeight: 152,
        padding: "28px 30px",
    },
    iconBox: {
        alignItems: "center",
        background: "#F0F1F4",
        borderRadius: 12,
        color: "#737780",
        display: "inline-flex",
        height: 56,
        justifyContent: "center",
        width: 56,
        "& .material-symbols-outlined": {
            fontSize: 34,
            fontVariationSettings: '"FILL" 0, "wght" 100, "GRAD" 0, "opsz" 32',
        },
    },
    statLabel: {
        color: "#8D9098",
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 1.25,
        margin: 0,
    },
    statValue: {
        color: "#202124",
        fontSize: 32,
        fontWeight: 700,
        lineHeight: 1.1,
        margin: "8px 0 0",
    },
    sectionCard: {
        borderRadius: 18,
        marginBottom: 32,
        padding: "28px 30px 30px",
    },
    sectionHeader: {
        alignItems: "flex-start",
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 12,
    },
    sectionTitle: {
        color: "#202124",
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.2,
        margin: 0,
    },
    sectionSubtitle: {
        color: "#969AA2",
        fontSize: 16,
        lineHeight: 1.3,
        margin: "4px 0 0",
    },
    salesLayout: {
        display: "grid",
        gap: 58,
        gridTemplateColumns: "380px minmax(0, 1fr)",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
        },
    },
    locationRow: {
        marginTop: 16,
    },
    locationMeta: {
        alignItems: "center",
        color: "#565A62",
        display: "flex",
        fontSize: 18,
        justifyContent: "space-between",
        lineHeight: 1.3,
        marginBottom: 7,
    },
    barTrack: {
        background: "#EEEFF1",
        borderRadius: 999,
        height: 8,
        overflow: "hidden",
    },
    barFill: {
        background: "#232427",
        borderRadius: 999,
        height: "100%",
    },
    mapShell: {
        background: "#F7F7F9",
        border: "1px solid #E8E8EE",
        borderRadius: 16,
        height: 260,
        overflow: "hidden",
        position: "relative",
        "& .jvectormap-container": {
            height: "260px !important",
        },
    },
    mapLabel: {
        color: "#B2B4BB",
        fontSize: 16,
        position: "absolute",
        right: 24,
        top: 18,
        zIndex: 2,
    },
    viewAll: {
        color: "#8D9098",
        fontSize: 16,
        textDecoration: "none",
    },
    emptyRow: {
        color: "#969AA2",
        fontSize: 15,
        padding: "26px 0",
        textAlign: "center",
    },
}));

function isShadcnSkinActive() {
    return isAdminUiSkinEnabled();
}

function StatTile({ icon, label, value }) {
    const classes = useShadcnStyles();

    return (
        <Paper elevation={1} className={classes.statCard}>
            <span className={classes.iconBox}>
                <MaterialSymbol>{icon}</MaterialSymbol>
            </span>
            <span>
                <p className={classes.statLabel}>{label}</p>
                <p className={classes.statValue}>{value}</p>
            </span>
        </Paper>
    );
}

function getOrderField(order, keys, fallback = "") {
    const key = keys.find((candidate) => order && order[candidate] !== undefined && order[candidate] !== null);
    return key ? order[key] : fallback;
}

function ShadcnDashboard({
    statistics,
    fromDate,
    toDate,
    onFilterChange,
    stateDataMap,
    formattedNumber,
    dealer,
    recentOrders,
}) {
    const classes = useShadcnStyles();
    const orderAmount = Number(statistics.orderAmount || 0);
    const orderCount = Number(statistics.orderCount || 0);
    const averageOrderValue = orderCount > 0 ? orderAmount / orderCount : 0;
    const topLocations = (statistics.orderStats || []).slice(0, 5);
    const maxLocationValue = Math.max(...topLocations.map((item) => Number(item[1]) || 0), 1);

    return (
        <div className={classes.page}>
            <Paper elevation={1} className={classes.filterCard}>
                <OrderFilterSelector onChange={onFilterChange} />
            </Paper>

            <div className={classes.statsGrid}>
                <StatTile icon="receipt_long" label="New Orders" value={orderCount} />
                <StatTile icon="attach_money" label="Orders Amount" value={formattedNumber} />
                <StatTile
                    icon="monitoring"
                    label="Avg. Order Value"
                    value={`$${financial(averageOrderValue)}`}
                />
                <StatTile icon="person" label="Active Customers" value={statistics.actives || 0} />
            </div>

            <Paper elevation={1} className={classes.sectionCard}>
                <div className={classes.sectionHeader}>
                    <div>
                        <h2 className={classes.sectionTitle}>Sales by Top Locations</h2>
                        <p className={classes.sectionSubtitle}>
                            Top states · {fromDate} – {toDate}
                        </p>
                    </div>
                </div>
                {dealer?.country === "CAN" ? (
                    <Typography variant="h5" gutterBottom style={{ textAlign: "center", margin: 32 }}>
                        Coming soon
                    </Typography>
                ) : (
                    <div className={classes.salesLayout}>
                        <div>
                            {topLocations.length === 0 ? (
                                <div className={classes.emptyRow}>No location sales for this period.</div>
                            ) : (
                                topLocations.map(([stateCode, value]) => {
                                    const safeValue = Number(value) || 0;
                                    return (
                                        <div className={classes.locationRow} key={stateCode}>
                                            <div className={classes.locationMeta}>
                                                <span>{stateCode}</span>
                                                <strong>${financial(safeValue)}</strong>
                                            </div>
                                            <div className={classes.barTrack}>
                                                <div
                                                    className={classes.barFill}
                                                    style={{
                                                        width: `${Math.max(
                                                            8,
                                                            (safeValue / maxLocationValue) * 100
                                                        )}%`,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                        <div className={classes.mapShell}>
                            <span className={classes.mapLabel}>U.S. sales map</span>
                            <VectorMap
                                map={"us_aea"}
                                backgroundColor="transparent"
                                zoomOnScroll={false}
                                containerStyle={{ width: "100%", height: "260px" }}
                                containerClassName="map"
                                regionStyle={{
                                    initial: {
                                        fill: "#E7E8EB",
                                        "fill-opacity": 1,
                                        stroke: "#ffffff",
                                        "stroke-width": 1,
                                        "stroke-opacity": 1,
                                    },
                                }}
                                series={{
                                    regions: [
                                        {
                                            values: stateDataMap,
                                            scale: ["#D3D5DA", "#222326"],
                                            normalizeFunction: "polynomial",
                                        },
                                    ],
                                }}
                            />
                        </div>
                    </div>
                )}
            </Paper>

            <Paper elevation={1} className={classes.sectionCard} style={{ display: "none" }}>
                <div className={classes.sectionHeader}>
                    <h2 className={classes.sectionTitle}>Recent Orders</h2>
                    <a className={classes.viewAll} href="/admin/orders">
                        View all
                    </a>
                </div>
                <MuiTable style={{ display: "none" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Order #</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell>Dealer</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {recentOrders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <div className={classes.emptyRow}>No recent orders for this period.</div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            recentOrders.map((order) => {
                                const id = getOrderField(order, ["id", "orderNumber"]);
                                const amount = Number(getOrderField(order, ["orderAmount", "total"], 0)) || 0;
                                return (
                                    <TableRow key={id}>
                                        <TableCell>#{getOrderField(order, ["orderNumber", "id"], "")}</TableCell>
                                        <TableCell>{getOrderField(order, ["billCompany", "firstName"], "")}</TableCell>
                                        <TableCell>{getOrderField(order, ["dealerCompany", "shipCompany", "sendTo"], "")}</TableCell>
                                        <TableCell>{getOrderField(order, ["date", "createdAtParsed", "createdAt"], "")}</TableCell>
                                        <TableCell>{getOrderField(order, ["status", "orderStatus", "statusName"], "")}</TableCell>
                                        <TableCell align="right">${financial(amount)}</TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </MuiTable>
            </Paper>
        </div>
    );
}

function StatisticCard({
    color = "warning",
    iconText = "content_copy",
    title = "",
    qty = "",
    qtyTitle = "",
    helperText = "",
    linkEnabled = false,
    linkTo = "",
}) {
    const classes = useStyles();

    const handleClick = (e) => {
        e.preventDefault();
        location.href = linkTo;
    };

    const content = (
        <Card>
            <CardHeader stats icon>
                <CardIcon color={color}>
                    <Icon>{iconText}</Icon>
                </CardIcon>
                <p className={classes.cardCategory}>{title}</p>
                <h3 className={classes.cardTitle}>
                    {qty} <small>{qtyTitle}</small>
                </h3>
            </CardHeader>
            <CardFooter stats>
                <div className={classes.stats} style={{ display: "flex", alignContent: "center" }}>
                    <DateRange />
                    <span style={{ marginTop: 2 }}>{helperText}</span>
                </div>
            </CardFooter>
        </Card>
    );

    return linkEnabled ? (
        <Link underline="none" component="div" to={linkTo} onClick={handleClick}>
            <a href={linkTo}>{content}</a>
        </Link>
    ) : (
        <div>{content}</div>
    );
}

export default function Dashboard() {
    const classes = useStyles();
    const [statistics, setStatistics] = useState({
        actives: 0,
        archived: 0,
        orderCount: 0,
        orderAmount: 0,
        orderStats: [],
    });
    const [recentOrders, setRecentOrders] = useState([]);
    const dealer = useDealer();
    const [fromDate, setFromDate] = useState(moment().subtract(7, "days").format("MM/DD/YYYY"));
    const [toDate, setToDate] = useState(moment().format("MM/DD/YYYY"));

    useEffect(() => {
        return () => {
            setStatistics({
                actives: 0,
                archived: 0,
                orderCount: 0,
                orderAmount: 0,
                orderStats: [],
            });
            setRecentOrders([]);
        };
    }, []);

    const getStatistics = async ({
        dlid = "",
        cid = "",
        fromDate = "",
        toDate = "",
        statusId = "",
        search = "",
    }) => {
        const [statistics, orders] = await Promise.all([
            DashboardService.getAll({
                dlid,
                cid,
                fromDate,
                toDate,
                statusId,
                search,
            }).then((d) => d.data),
            OrderService.getAll(0, 3, fromDate, toDate, statusId, search, cid, dlid).then(
                (d) => d.data
            ),
        ]);
        setStatistics(statistics);
        setRecentOrders(orders?.data || []);
    };

    const handleFilterChange = (dealerId, customerId, fromDate, toDate, statusId) => {
        setFromDate(fromDate);
        setToDate(toDate);
        getStatistics({ dlid: dealerId, cid: customerId, fromDate, toDate, statusId });
    };

    const stateDataMap = statistics.orderStats.reduce((result, item) => {
        const [stateCode, quantity] = item;
        const key = `US-${stateCode}`;
        result[key] = quantity;
        return result;
    }, {});

    const formattedNumber = statistics.orderAmount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
    });

    if (isShadcnSkinActive()) {
        return (
            <ShadcnDashboard
                statistics={statistics}
                fromDate={fromDate}
                toDate={toDate}
                onFilterChange={handleFilterChange}
                stateDataMap={stateDataMap}
                formattedNumber={formattedNumber}
                dealer={dealer}
                recentOrders={recentOrders}
            />
        );
    }

    return (
        <>
            <GridContainer>
                <GridItem xs={12} sm={6} md={6} lg={12}>
                    <Card style={{ padding: 10 }}>
                        <OrderFilterSelector onChange={handleFilterChange} />
                    </Card>
                </GridItem>
            </GridContainer>
            {/* */}
            <GridContainer>
                {/*<GridItem xs={12} sm={6} md={6} lg={3}>
                    <StatisticCard
                        color="success"
                        iconText="store"
                        qty={statistics.actives}
                        title="Active Stores"
                        helperText="10/25/2023 to 10/25/2023"
                    />
                </GridItem>
                <GridItem xs={12} sm={6} md={6} lg={3}>
                    <StatisticCard
                        iconText="archive"
                        title="Archived Stores"
                        qty={statistics.archived}
                        qtyTitle=""
                        helperText="10/25/2023 to 10/25/2023"
                    />
                </GridItem>*/}
                <GridItem xs={12} sm={6} md={6} lg={3}>
                    <StatisticCard
                        linkEnabled
                        linkTo="/admin/orders"
                        color="success"
                        title="New Orders"
                        qty={statistics.orderCount}
                        qtyTitle="orders"
                        helperText={`${fromDate} to ${toDate}`}
                    />
                </GridItem>
                <GridItem xs={12} sm={6} md={6} lg={3}>
                    <StatisticCard
                        iconText="attach_money"
                        title="Orders Amount"
                        qty={formattedNumber}
                        helperText={`${fromDate} to ${toDate}`}
                    />
                </GridItem>
            </GridContainer>
            {/* */}
            <GridContainer>
                <Card>
                    <CardHeader color="success" icon>
                        <CardIcon color="success">
                            <Icon>language</Icon>
                        </CardIcon>
                        <h4 className={classes.cardIconTitle}>Global Sales by Top Locations</h4>
                    </CardHeader>
                    <CardBody>
                        {dealer?.country == "CAN" ? (
                            <Typography
                                variant="h5"
                                gutterBottom
                                style={{ textAlign: "center", marginTop: 25, marginBottom: 25 }}
                            >
                                Coming soon
                            </Typography>
                        ) : null}

                        <GridContainer justify="space-between">
                            <GridItem xs={12} sm={12} md={5}>
                                <LegacyTable tableData={statistics.orderStats} />
                            </GridItem>

                            {dealer?.country !== "CAN" ? (
                                <GridItem xs={12} sm={12} md={6}>
                                    <VectorMap
                                        map={"us_aea"}
                                        backgroundColor="transparent"
                                        zoomOnScroll={false}
                                        containerStyle={{
                                            width: "100%",
                                            height: "280px",
                                        }}
                                        containerClassName="map"
                                        regionStyle={{
                                            initial: {
                                                fill: "#e4e4e4",
                                                "fill-opacity": 0.9,
                                                stroke: "none",
                                                "stroke-width": 0,
                                                "stroke-opacity": 0,
                                            },
                                        }}
                                        series={{
                                            regions: [
                                                {
                                                    values: stateDataMap,
                                                    scale: ["#AAAAAA", "#444444"],
                                                    normalizeFunction: "polynomial",
                                                },
                                            ],
                                        }}
                                    />
                                </GridItem>
                            ) : null}
                        </GridContainer>
                    </CardBody>
                </Card>
            </GridContainer>
        </>
    );
}
